import React, { useState } from 'react';
import { Sparkles, Loader2, Trash2, RefreshCcw } from 'lucide-react';
import { RichTextToolbar, UploadButton, AnswerAndDuration } from './shared';
import { aiApi } from '../../../lib/api';
import { useToast } from '../../../hooks/use-toast';

// ---- Shared helpers ----------------------------------------------------------

function GeneratedExerciseBlock({ content, onClear, testid }) {
  if (!content) return null;
  return (
    <div className="mt-4 rounded-xl border border-[#E9E3FB] bg-[#FAF8FF] p-3" data-testid={testid}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-[12.5px] font-semibold text-[#1A1533] inline-flex items-center gap-1.5">
          <Sparkles size={13} className="text-[#5B4FE8]" />
          AI-generated exercise
        </span>
        <button
          onClick={onClear}
          className="text-[11.5px] text-[#8A83A5] hover:text-[#DC3545] inline-flex items-center gap-1"
        >
          <Trash2 size={11} /> Clear
        </button>
      </div>
      <div className="rounded-lg bg-white border border-[#ECE6FA] p-3 max-h-[280px] overflow-y-auto whitespace-pre-wrap text-[12.5px] leading-relaxed text-[#1A1533]">
        {content}
      </div>
    </div>
  );
}

function useExerciseAi(module, onChange, exerciseType) {
  const { toast } = useToast();
  const [generating, setGenerating] = useState(false);

  const generate = async ({ topic, context }) => {
    if (!topic?.trim() || generating) return;
    setGenerating(true);
    try {
      const res = await aiApi.exercise({
        exercise_type: exerciseType,
        topic: topic.trim(),
        context: context || '',
      });
      onChange({ generatedContent: res.content, topic: topic.trim() });
      toast({ title: 'Exercise generated', description: 'Review the draft below — you can edit before saving.' });
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || 'AI service is unavailable.';
      toast({ title: 'Generation failed', description: msg });
    } finally {
      setGenerating(false);
    }
  };

  const clear = () => onChange({ generatedContent: '' });
  return { generating, generate, clear, hasContent: Boolean(module.generatedContent) };
}

// ---- Forms -------------------------------------------------------------------

export function CustomExerciseForm({ module, onChange }) {
  const [topic, setTopic] = useState(module.topic || '');
  const { generating, generate, clear, hasContent } = useExerciseAi(module, onChange, 'custom');

  return (
    <div>
      <p className="text-[13px] font-semibold text-[#1A1533] mb-2">Define the exercise below or upload a pdf</p>
      <RichTextToolbar />
      <textarea
        value={module.content || ''}
        onChange={(e) => onChange({ content: e.target.value })}
        placeholder="Enter here"
        rows={6}
        className="w-full px-3 py-2.5 rounded-b-lg border border-[#ECE6FA] bg-white outline-none text-[13.5px] placeholder-[#9A93B0] focus:ring-2 focus:ring-[#5B4FE8]/30 resize-none"
      />
      <UploadButton />

      <div className="mt-5 rounded-xl border border-[#E9E3FB] bg-[#FAF8FF] p-3">
        <div className="flex items-center gap-2 mb-2">
          <Sparkles size={13} className="text-[#5B4FE8]" />
          <span className="text-[12.5px] font-semibold text-[#1A1533]">Or let AI draft it for you</span>
        </div>
        <input
          value={topic}
          onChange={(e) => setTopic(e.target.value)}
          placeholder="Topic (e.g. SQL data modeling, sales negotiation, system design)"
          data-testid="custom-exercise-topic"
          className="w-full px-3 py-2.5 rounded-lg border border-[#ECE6FA] bg-white outline-none text-[13px] focus:ring-2 focus:ring-[#5B4FE8]/30"
        />
        <button
          onClick={() => generate({ topic, context: module.content || '' })}
          disabled={!topic.trim() || generating}
          data-testid="custom-exercise-generate-btn"
          className="mt-2 inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-gradient-to-r from-[#5B4FE8] to-[#7A6BFF] text-white text-[12.5px] font-semibold shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed transition-all"
        >
          {generating ? <Loader2 size={12} className="animate-spin" /> : (hasContent ? <RefreshCcw size={12} /> : <Sparkles size={12} />)}
          {generating ? 'Generating…' : (hasContent ? 'Regenerate exercise' : 'Generate exercise with AI')}
        </button>
      </div>

      <GeneratedExerciseBlock
        content={module.generatedContent}
        onClear={clear}
        testid="custom-exercise-generated"
      />
      <AnswerAndDuration module={module} onChange={onChange} />
    </div>
  );
}

export function CodingExerciseForm({ module, onChange }) {
  const { generating, generate, clear, hasContent } = useExerciseAi(module, onChange, 'coding');

  return (
    <div>
      <div className="inline-flex rounded-full bg-[#F1ECFE] p-1 mb-3">
        <button className="px-4 py-1.5 rounded-full text-[12.5px] font-semibold bg-white text-[#5B4FE8] shadow-sm">Basic exercise (DSA-based)</button>
        <button className="px-4 py-1.5 rounded-full text-[12.5px] font-semibold text-[#4A4463]">Problem solving exercise</button>
      </div>
      <p className="text-[12.5px] font-semibold text-[#1A1533]">Add more details to tailor the coding exercise (Optional)</p>
      <textarea
        value={module.content || ''}
        onChange={(e) => onChange({ content: e.target.value })}
        placeholder="e.g. Make the DSA problem extremely difficult and focus on a problem that will require recursion to solve efficiently."
        rows={3}
        className="mt-2 w-full px-3 py-2.5 rounded-lg border border-[#ECE6FA] bg-white outline-none text-[13px] focus:ring-2 focus:ring-[#5B4FE8]/30 resize-none"
        data-testid="coding-exercise-context"
      />
      <button
        onClick={() => generate({
          topic: 'Coding interview',
          context: module.content || '',
        })}
        disabled={generating}
        data-testid="coding-exercise-generate-btn"
        className="mt-3 w-full py-2.5 rounded-lg bg-gradient-to-r from-[#5B4FE8] to-[#7A6BFF] text-white text-[12.5px] font-semibold inline-flex items-center justify-center gap-2 hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed transition-all"
      >
        {generating ? <Loader2 size={14} className="animate-spin" /> : (hasContent ? <RefreshCcw size={14} /> : <Sparkles size={14} />)}
        {generating ? 'Generating…' : (hasContent ? 'Regenerate coding exercise' : 'Generate coding exercise with AI')}
      </button>
      <GeneratedExerciseBlock
        content={module.generatedContent}
        onClear={clear}
        testid="coding-exercise-generated"
      />
      <AnswerAndDuration module={module} onChange={onChange} />
    </div>
  );
}

export function HumanDataForm({ module, onChange }) {
  return (
    <div>
      <p className="text-[12.5px] text-[#4A4463] leading-relaxed">
        After conversational interview there will be a 25 mins data annotation exercise. Please define that below.
      </p>
      <label className="mt-3 block text-[12.5px] font-semibold text-[#1A1533]">Which subject matter expert you are interviewing for?</label>
      <input
        value={module.topic || ''}
        onChange={(e) => onChange({ topic: e.target.value })}
        placeholder="e.g. Medicine, English, Competitive coding etc."
        className="mt-1.5 w-full px-3 py-2.5 rounded-lg border border-[#ECE6FA] bg-white outline-none text-[13px] focus:ring-2 focus:ring-[#5B4FE8]/30"
      />
      <label className="mt-3 block text-[12.5px] font-semibold text-[#1A1533]">Give any context for tailored exercise (Optional)</label>
      <textarea
        value={module.content || ''}
        onChange={(e) => onChange({ content: e.target.value })}
        placeholder="This context will be used to generate tailored human data exercise."
        rows={3}
        className="mt-1.5 w-full px-3 py-2.5 rounded-lg border border-[#ECE6FA] bg-white outline-none text-[13px] focus:ring-2 focus:ring-[#5B4FE8]/30 resize-none"
      />
      <AnswerAndDuration module={module} onChange={onChange} hideAnswerMode />
    </div>
  );
}

export function SpeechToTextForm({ module, onChange }) {
  return (
    <div>
      <div className="inline-flex rounded-full bg-[#F1ECFE] p-1 mb-3">
        <button className="px-4 py-1.5 rounded-full text-[12.5px] font-semibold bg-white text-[#5B4FE8] shadow-sm">General exercise</button>
        <button className="px-4 py-1.5 rounded-full text-[12.5px] font-semibold text-[#4A4463]">Pre-define audio exercise</button>
      </div>
      <p className="text-[13px] font-semibold text-[#1A1533] mb-2">Define the exercise below or upload a pdf</p>
      <RichTextToolbar />
      <textarea
        value={module.content || ''}
        onChange={(e) => onChange({ content: e.target.value })}
        placeholder="Enter here"
        rows={5}
        className="w-full px-3 py-2.5 rounded-b-lg border border-[#ECE6FA] bg-white outline-none text-[13.5px] focus:ring-2 focus:ring-[#5B4FE8]/30 resize-none"
      />
      <AnswerAndDuration module={module} onChange={onChange} hideAnswerMode />
    </div>
  );
}

export function McqForm({ module, onChange }) {
  const { toast } = useToast();
  const [generating, setGenerating] = useState(false);
  const mcqs = module.mcqs || [];
  const context = module.content || '';

  const generate = async () => {
    if (!context.trim() || generating) return;
    setGenerating(true);
    try {
      const res = await aiApi.mcqs({ context: context.trim(), count: 5 });
      const items = Array.isArray(res?.questions) ? res.questions : [];
      if (items.length === 0) {
        toast({ title: 'No MCQs generated', description: 'AI returned an empty list. Refine the context and try again.' });
        return;
      }
      onChange({ mcqs: items });
      toast({ title: 'MCQs generated', description: `${items.length} questions added.` });
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || 'AI service is unavailable.';
      toast({ title: 'Generation failed', description: msg });
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div>
      <div className="inline-flex rounded-full bg-[#F1ECFE] p-1 mb-3">
        <button className="px-4 py-1.5 rounded-full text-[12.5px] font-semibold bg-white text-[#5B4FE8] shadow-sm">AI generated</button>
        <button className="px-4 py-1.5 rounded-full text-[12.5px] font-semibold text-[#4A4463]">Custom questions</button>
      </div>
      <label className="block text-[12.5px] font-semibold text-[#1A1533]">Enter context to guide question generation</label>
      <textarea
        value={context}
        onChange={(e) => onChange({ content: e.target.value })}
        placeholder="e.g. Generate 5 MCQs to test a candidate's knowledge of core finance concepts including financial statements, ratios, valuation methods, time value of money, and corporate finance."
        rows={4}
        className="mt-1.5 w-full px-3 py-2.5 rounded-lg border border-[#ECE6FA] bg-white outline-none text-[13px] focus:ring-2 focus:ring-[#5B4FE8]/30 resize-none"
        data-testid="mcq-context-input"
      />
      <button
        onClick={generate}
        disabled={!context.trim() || generating}
        data-testid="mcq-generate-btn"
        className="mt-3 inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-gradient-to-r from-[#5B4FE8] to-[#7A6BFF] text-white text-[12.5px] font-semibold shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed transition-all"
      >
        {generating ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
        {generating ? 'Generating…' : (mcqs.length ? `Regenerate ${mcqs.length} MCQs` : 'Generate 5 MCQs with AI')}
      </button>

      {mcqs.length > 0 && (
        <div className="mt-4 rounded-xl border border-[#E9E3FB] bg-[#FAF8FF] p-3" data-testid="mcq-generated-list">
          <div className="flex items-center justify-between">
            <span className="text-[12.5px] font-semibold text-[#1A1533] inline-flex items-center gap-1.5">
              <Sparkles size={13} className="text-[#5B4FE8]" />
              AI-generated MCQs ({mcqs.length})
            </span>
            <button
              onClick={() => onChange({ mcqs: [] })}
              className="text-[11.5px] text-[#8A83A5] hover:text-[#DC3545] inline-flex items-center gap-1"
            >
              <Trash2 size={11} /> Clear all
            </button>
          </div>
          <ol className="mt-2 space-y-2">
            {mcqs.map((m, i) => (
              <li key={i} className="rounded-lg bg-white border border-[#ECE6FA] p-3" data-testid={`mcq-card-${i}`}>
                <div className="flex items-start gap-2">
                  <span className="shrink-0 mt-1 text-[11.5px] font-bold text-[#5B4FE8]">{i + 1}.</span>
                  <textarea
                    value={m.q}
                    onChange={(e) => onChange({ mcqs: mcqs.map((x, j) => j === i ? { ...x, q: e.target.value } : x) })}
                    rows={2}
                    data-testid={`mcq-q-input-${i}`}
                    className="flex-1 text-[13px] font-semibold text-[#1A1533] leading-snug bg-transparent border border-transparent hover:border-[#ECE6FA] focus:border-[#5B4FE8] focus:bg-[#FAF8FF] rounded-md px-2 py-1.5 outline-none resize-none transition-colors"
                  />
                  <button
                    onClick={() => onChange({ mcqs: mcqs.filter((_, j) => j !== i) })}
                    className="shrink-0 w-6 h-6 rounded-md flex items-center justify-center text-[#DC3545] hover:bg-[#FDECEC]"
                    aria-label="Remove MCQ"
                  >
                    <Trash2 size={12} />
                  </button>
                </div>
                <ul className="mt-2 space-y-1.5 pl-4">
                  {(m.options || []).map((opt, j) => (
                    <li key={j} className="flex items-center gap-2">
                      <input
                        type="radio"
                        name={`mcq-${i}-correct`}
                        checked={j === m.correct}
                        onChange={() => onChange({ mcqs: mcqs.map((x, k) => k === i ? { ...x, correct: j } : x) })}
                        data-testid={`mcq-correct-${i}-${j}`}
                        className="accent-[#1F8A4E] shrink-0"
                        aria-label={`Mark option ${String.fromCharCode(65 + j)} as correct`}
                      />
                      <span className={`text-[11.5px] font-bold w-4 ${j === m.correct ? 'text-[#1F8A4E]' : 'text-[#8A83A5]'}`}>
                        {String.fromCharCode(65 + j)}.
                      </span>
                      <input
                        type="text"
                        value={opt}
                        onChange={(e) => onChange({
                          mcqs: mcqs.map((x, k) => k === i
                            ? { ...x, options: x.options.map((o, l) => l === j ? e.target.value : o) }
                            : x),
                        })}
                        data-testid={`mcq-option-input-${i}-${j}`}
                        className={`flex-1 text-[12.5px] leading-snug bg-transparent border border-transparent hover:border-[#ECE6FA] focus:border-[#5B4FE8] focus:bg-[#FAF8FF] rounded-md px-2 py-1 outline-none transition-colors ${j === m.correct ? 'text-[#1F8A4E] font-semibold' : 'text-[#4A4463]'}`}
                      />
                    </li>
                  ))}
                </ul>
              </li>
            ))}
          </ol>
        </div>
      )}

      <AnswerAndDuration module={module} onChange={onChange} hideAnswerMode />
    </div>
  );
}

export function BigFiveForm({ module, onChange }) {
  return (
    <div>
      <p className="text-[12.5px] text-[#4A4463] leading-relaxed">
        This environment explores how candidates score across the Big Five personality traits: Openness, Conscientiousness, Extraversion, Agreeableness, and Neuroticism. You set the kind of behaviors you value most, and Zara calculates a percentage match to show how each candidate fits those expectations.
      </p>
      <label className="mt-3 block text-[12.5px] font-semibold text-[#1A1533]">Tell us about what kind of behaviors are you looking for in candidates? Your input helps Zara generate a 10 minute survey. (Optional)</label>
      <textarea
        value={module.content || ''}
        onChange={(e) => onChange({ content: e.target.value })}
        placeholder="e.g. We're looking for someone who is highly conscientious, open to new ideas, and works well in a team setting."
        rows={3}
        className="mt-1.5 w-full px-3 py-2.5 rounded-lg border border-[#ECE6FA] bg-white outline-none text-[13px] focus:ring-2 focus:ring-[#5B4FE8]/30 resize-none"
      />
      <AnswerAndDuration module={module} onChange={onChange} hideAnswerMode />
    </div>
  );
}

export function OnTheFlyForm({ module, onChange }) {
  const { generating, generate, clear, hasContent } = useExerciseAi(module, onChange, 'on-the-fly');
  const topic = module.topic || '';
  const taskDesc = module.content || '';
  const grading = module.gradingNotes || '';

  return (
    <div className="rounded-xl border border-[#ECE6FA] bg-[#FAF8FF] p-5">
      <h4 className="text-[14px] font-bold text-[#1A1533] inline-flex items-center gap-2">
        On the fly exercise set up
        <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-[#E9E3FB] text-[#5B4FE8]">Beta</span>
      </h4>
      <label className="mt-3 block text-[12.5px] font-semibold text-[#1A1533]">Topic</label>
      <input
        value={topic}
        onChange={(e) => onChange({ topic: e.target.value })}
        placeholder="Enter here"
        data-testid="otf-topic"
        className="mt-1.5 w-full px-3 py-2.5 rounded-lg border border-[#ECE6FA] bg-white outline-none text-[13px] focus:ring-2 focus:ring-[#5B4FE8]/30"
      />
      <label className="mt-3 block text-[12.5px] font-semibold text-[#1A1533]">Describe the task, and we'll handle the rest, from prompt to interface</label>
      <textarea
        value={taskDesc}
        onChange={(e) => onChange({ content: e.target.value })}
        placeholder="e.g. A dataset with messy order records with issues like missing values, wrong date formats, duplicates, and inconsistent column names."
        rows={3}
        className="mt-1.5 w-full px-3 py-2.5 rounded-lg border border-[#ECE6FA] bg-white outline-none text-[13px] focus:ring-2 focus:ring-[#5B4FE8]/30 resize-none"
      />
      <label className="mt-3 block text-[12.5px] font-semibold text-[#1A1533]">How would you like this to be graded? What are the main things Zara should care for? (Optional)</label>
      <textarea
        value={grading}
        onChange={(e) => onChange({ gradingNotes: e.target.value })}
        placeholder="e.g. Zara should check for accuracy, logical reasoning, and completeness of the solution."
        rows={2}
        className="mt-1.5 w-full px-3 py-2.5 rounded-lg border border-[#ECE6FA] bg-white outline-none text-[13px] focus:ring-2 focus:ring-[#5B4FE8]/30 resize-none"
      />
      <AnswerAndDuration module={module} onChange={onChange} hideAnswerMode />
      <button
        onClick={() => generate({
          topic,
          context: [taskDesc, grading ? `Grading: ${grading}` : ''].filter(Boolean).join('\n'),
        })}
        disabled={!topic.trim() || generating}
        data-testid="otf-generate-btn"
        className="mt-4 w-full py-2.5 rounded-full bg-gradient-to-r from-[#5B4FE8] to-[#7A6BFF] text-white text-[13px] font-semibold inline-flex items-center justify-center gap-2 hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed transition-all"
      >
        {generating ? <Loader2 size={14} className="animate-spin" /> : (hasContent ? <RefreshCcw size={14} /> : <Sparkles size={14} />)}
        {generating ? 'Generating…' : (hasContent ? 'Regenerate exercise' : 'Generate exercise')}
      </button>
      <GeneratedExerciseBlock
        content={module.generatedContent}
        onClear={clear}
        testid="otf-generated"
      />
    </div>
  );
}
